import { httpClient } from '@/api/httpClient';
import {
  mapPaginatedResource,
  type PaginatedApiResource,
  type PaginatedResult,
} from '@/api/pagination.types';
import {
  mapApiResourceToBroadcast,
  mapApiResourceToBroadcastDetail,
  mapCreateBroadcastPayload,
  type AudiencePreview,
  type Broadcast,
  type BroadcastApiResource,
  type BroadcastAudienceKind,
  type BroadcastPurpose,
  type BroadcastDetail,
  type BroadcastDetailApiResource,
  type CreateBroadcastPayload,
} from './broadcastsApi.types';

const BASE_PATH = '/me/workspace/broadcasts';

export async function fetchBroadcasts(
  page: number,
  pageSize: number
): Promise<PaginatedResult<Broadcast>> {
  const response = await httpClient.get<PaginatedApiResource<BroadcastApiResource>>(BASE_PATH, {
    params: { page, page_size: pageSize },
  });
  return mapPaginatedResource(response.data, mapApiResourceToBroadcast);
}

export async function fetchBroadcast(broadcastId: string): Promise<BroadcastDetail> {
  const response = await httpClient.get<BroadcastDetailApiResource>(BASE_PATH + '/' + broadcastId);
  return mapApiResourceToBroadcastDetail(response.data);
}

export async function createBroadcast(payload: CreateBroadcastPayload): Promise<Broadcast> {
  const response = await httpClient.post<BroadcastApiResource>(
    BASE_PATH,
    mapCreateBroadcastPayload(payload)
  );
  return mapApiResourceToBroadcast(response.data);
}

export async function previewBroadcastAudience(
  audienceKind: BroadcastAudienceKind,
  purpose: BroadcastPurpose,
  selectedClientIds: string[] | null
): Promise<AudiencePreview> {
  const response = await httpClient.post<{
    total_recipients: number;
    consented_recipients: number;
  }>(BASE_PATH + '/preview', {
    audience_kind: audienceKind,
    purpose,
    selected_client_ids: selectedClientIds,
  });
  return {
    totalRecipients: response.data.total_recipients,
    consentedRecipients: response.data.consented_recipients,
  };
}

export async function sendBroadcast(
  broadcastId: string,
  scheduledFor: string | null
): Promise<Broadcast> {
  const response = await httpClient.post<BroadcastApiResource>(
    BASE_PATH + '/' + broadcastId + '/send',
    { scheduled_for: scheduledFor }
  );
  return mapApiResourceToBroadcast(response.data);
}

export async function cancelBroadcast(broadcastId: string): Promise<Broadcast> {
  const response = await httpClient.post<BroadcastApiResource>(
    BASE_PATH + '/' + broadcastId + '/cancel'
  );
  return mapApiResourceToBroadcast(response.data);
}
